import type { SharedItineraryResponse } from '@elara/domain/models/schemas/shared-itinerary-schemas';

import type { SessionState } from './SessionState.js';

import { buildMarketingSiteUrl, buildMembershipApplicationUrl } from '@elara/domain/constants';
import { SharedItineraryResponseSchema } from '@elara/domain/models/schemas/shared-itinerary-schemas';

import { getEnv } from '#env-schema.js';
import {
  trackApplicationStarted,
  trackMembershipLearnMore,
  trackSharedItineraryPrinted,
  trackSharedItineraryViewed,
} from '#lib/analytics-events.js';
import { getErrorReporter } from '#lib/error-reporting.js';
import { performFlowFetch, toAbsolutePath } from '#lib/flow-utils.js';
import { createState } from '#lib/signals-utils.js';

/** Where on the shared page a membership call-to-action sits. */
export type MembershipCtaPlacement = 'banner' | 'footer';

/** Load lifecycle of the shared itinerary page. */
export type SharedItineraryStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; itinerary: SharedItineraryResponse }
  | { kind: 'not_found' }
  | { kind: 'error' };

/** A shared page left open in a background tab refetches once it has been hidden this long. */
export const SHARED_ITINERARY_STALE_MS = 5 * 60 * 1000;

const ENDPOINT = '/api/share/itinerary';

/** Classifies the trip by date for analytics, without sending the dates themselves. */
function tripStatusOf(
  itinerary: SharedItineraryResponse,
  today: string,
): 'upcoming' | 'in_progress' | 'past' | 'undated' {
  const start = itinerary.trip.startDate?.slice(0, 10);
  const end = itinerary.trip.endDate?.slice(0, 10) ?? start;
  if (!start || !end) return 'undated';
  if (today < start) return 'upcoming';
  if (today > end) return 'past';
  return 'in_progress';
}

/**
 * Store behind the public read-only itinerary page (`/share/:token`). The visitor is usually not
 * signed in, so this talks to the unauthenticated `POST /api/share/itinerary` endpoint directly
 * rather than through the token-gated flow proxy — reusing the flow transport's base-URL and
 * injectable-fetch seams, like `InviteState`.
 *
 * @param sessionState - Read only to tag the view event with whether the visitor is signed in.
 * @param fetchImpl - Injectable fetch port; web falls back to `globalThis.fetch`.
 * @param now - Clock seam for tests.
 */
export function createSharedItineraryState(
  sessionState: Pick<SessionState, 'userId'>,
  fetchImpl?: typeof fetch,
  now: () => Date = () => new Date(),
) {
  // Guards against a slow response for an old token overwriting a newer one.
  let latestRequest = 0;

  return createState({
    status: { kind: 'idle' } as SharedItineraryStatus,
    /** The token currently displayed (kept so a background refresh can reload it). */
    token: undefined as string | undefined,
    /** Epoch ms of the last successful load. */
    loadedAt: undefined as number | undefined,

    /** The loaded itinerary, or `undefined` until `status` is `ready`. */
    get itinerary(): SharedItineraryResponse | undefined {
      return this.status.kind === 'ready' ? this.status.itinerary : undefined;
    },

    /**
     * Loads the shared itinerary for `token`. A `silent` reload keeps the current page on screen
     * (no loading state) and only replaces it when the response arrives.
     */
    async load(token: string, options: { silent?: boolean } = {}): Promise<void> {
      const requestId = ++latestRequest;
      const isNewToken = token !== this.token;
      this.token = token;
      if (!options.silent || isNewToken) this.status = { kind: 'loading' };

      try {
        const baseUrl = getEnv().VITE_FLOW_API_BASE_URL?.trim() || undefined;
        const response = await performFlowFetch(fetchImpl, toAbsolutePath(ENDPOINT, baseUrl), {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (requestId !== latestRequest) return;

        if (response.status === 404) {
          this.status = { kind: 'not_found' };
          return;
        }
        if (!response.ok) {
          throw new Error(`Shared itinerary request failed with status ${response.status}`);
        }

        const itinerary = SharedItineraryResponseSchema.parse(await response.json());
        if (requestId !== latestRequest) return;

        const isFirstView = this.status.kind !== 'ready';
        this.status = { kind: 'ready', itinerary };
        this.loadedAt = now().getTime();

        if (isFirstView) {
          trackSharedItineraryViewed({
            tripStatus: tripStatusOf(itinerary, now().toISOString().slice(0, 10)),
            dayCount: itinerary.days.length,
            isSignedIn: !!sessionState.userId,
          });
        }
      } catch (error) {
        if (requestId !== latestRequest) return;
        getErrorReporter().captureException(error, { tags: { module: 'SharedItineraryState', action: 'load' } });
        // A failed background refresh keeps the itinerary the visitor is already reading.
        if (!(options.silent && this.status.kind === 'ready')) {
          this.status = { kind: 'error' };
        }
      }
    },

    /** Reloads in place when the page has been open longer than {@link SHARED_ITINERARY_STALE_MS}. */
    async refreshIfStale(): Promise<void> {
      if (!this.token || this.status.kind !== 'ready' || this.loadedAt === undefined) return;
      if (now().getTime() - this.loadedAt < SHARED_ITINERARY_STALE_MS) return;
      await this.load(this.token, { silent: true });
    },

    /**
     * Membership application link for a call-to-action. Carries the owner's referral code so an
     * applicant credits the person who shared the trip.
     */
    membershipApplicationUrl(placement: MembershipCtaPlacement): string {
      return buildMembershipApplicationUrl({
        source: 'shared-itinerary',
        medium: 'share-link',
        campaign: 'trip-share',
        content: placement,
        ref: this.itinerary?.membership.referralCode,
      });
    },

    /** Records a membership call-to-action click. */
    recordMembershipCtaClicked(placement: MembershipCtaPlacement): void {
      trackApplicationStarted({ source: 'shared_itinerary', placement });
    },

    /** Marketing-site link for a "learn more" call-to-action, with the same attribution as the application link. */
    marketingSiteUrl(placement: MembershipCtaPlacement): string {
      return buildMarketingSiteUrl({
        source: 'shared-itinerary',
        medium: 'share-link',
        campaign: 'trip-share',
        content: placement,
        ref: this.itinerary?.membership.referralCode,
      });
    },

    /** Records that the visitor printed the itinerary from the page's Print button. */
    recordPrinted(): void {
      trackSharedItineraryPrinted();
    },

    /** Records a "learn more" click through to the marketing site. */
    recordLearnMoreClicked(placement: MembershipCtaPlacement): void {
      trackMembershipLearnMore({ source: 'shared_itinerary', placement });
    },
  });
}

export type SharedItineraryState = ReturnType<typeof createSharedItineraryState>;
